import { useAudioPlayer } from "../contexts/AudioPlayerContext";
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX } from "lucide-react";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { motion, AnimatePresence } from "framer-motion";

export default function AudioPlayer() {
  const {
    currentTrack,
    isPlaying,
    progress,
    duration,
    volume,
    togglePlay,
    playNext,
    playPrevious,
    seekTo,
    changeVolume,
  } = useAudioPlayer();

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <AnimatePresence>
      {currentTrack && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 z-50 glass player-gradient border-t border-white/10"
          data-testid="audio-player"
        >
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4">
            <div className="flex items-center gap-4 lg:gap-8">
              {/* Track Info */}
              <div className="flex items-center gap-4 min-w-0 flex-1 lg:flex-initial lg:w-64">
                <img
                  src={currentTrack.cover_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${currentTrack.cover_url}` : currentTrack.cover_url}
                  alt={currentTrack.title}
                  className="w-14 h-14 rounded-lg object-cover"
                  data-testid="player-cover"
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-heading font-bold text-sm truncate" data-testid="player-title">
                      {currentTrack.title}
                    </p>
                    <span className="text-[10px] font-bold uppercase tracking-wide bg-primary/20 text-primary px-1.5 py-0.5 rounded" data-testid="player-preview-tag">
                      Preview
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">
                    {currentTrack.bpm} BPM • {currentTrack.key}
                  </p>
                </div>
              </div>

              {/* Controls */}
              <div className="hidden lg:flex flex-col items-center gap-2 flex-1">
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={playPrevious}
                    className="hover:bg-white/10 rounded-full"
                    data-testid="player-prev"
                  >
                    <SkipBack className="w-5 h-5" />
                  </Button>
                  <Button
                    onClick={togglePlay}
                    size="icon"
                    className="w-12 h-12 rounded-full bg-primary hover:bg-primary/90 neon-glow"
                    data-testid="player-play-pause"
                  >
                    {isPlaying ? (
                      <Pause className="w-5 h-5" />
                    ) : (
                      <Play className="w-5 h-5 ml-0.5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={playNext}
                    className="hover:bg-white/10 rounded-full"
                    data-testid="player-next"
                  >
                    <SkipForward className="w-5 h-5" />
                  </Button>
                </div>

                {/* Progress Bar */}
                <div className="flex items-center gap-3 w-full max-w-xl">
                  <span className="text-xs text-muted-foreground font-mono w-10 text-right">
                    {formatTime((progress / 100) * duration)}
                  </span>
                  <div className="flex-1">
                    <Slider
                      value={[progress]}
                      max={100}
                      step={0.1}
                      onValueChange={([val]) => seekTo(val)}
                      className="cursor-pointer"
                      data-testid="player-progress"
                    />
                  </div>
                  <span className="text-xs text-muted-foreground font-mono w-10">
                    {formatTime(duration)}
                  </span>
                </div>
              </div>

              {/* Mobile Play Button */}
              <div className="lg:hidden">
                <Button
                  onClick={togglePlay}
                  size="icon"
                  className="w-12 h-12 rounded-full bg-primary hover:bg-primary/90"
                  data-testid="player-play-pause-mobile"
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5" />
                  ) : (
                    <Play className="w-5 h-5 ml-0.5" />
                  )}
                </Button>
              </div>

              {/* Volume */}
              <div className="hidden lg:flex items-center gap-3 w-36">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => changeVolume(volume === 0 ? 0.7 : 0)}
                  className="hover:bg-white/10 rounded-full"
                  data-testid="player-volume-toggle"
                >
                  {volume === 0 ? (
                    <VolumeX className="w-5 h-5" />
                  ) : (
                    <Volume2 className="w-5 h-5" />
                  )}
                </Button>
                <Slider
                  value={[volume * 100]}
                  max={100}
                  step={1}
                  onValueChange={([val]) => changeVolume(val / 100)}
                  className="cursor-pointer"
                  data-testid="player-volume"
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
